import styled from '@emotion/styled'
import { StyledComponent } from '@emotion/styled/macro'
import { HTMLAttributes } from 'react'
import {
  BorderProps,
  DisplayProps,
  SpaceProps,
  TextAlignProps,
  TextColorProps,
  TypographyProps,
  border,
  color,
  display,
  space,
  typography,
} from 'styled-system'

import { TestIdProps } from 'designSystem/types/propTypes'

import { FONT_SIZE } from 'designSystem/foundations/constants/fontSize'
import { FONT_WEIGHT } from 'designSystem/foundations/constants/fontWeight'
import { LINE_HEIGHT } from 'designSystem/foundations/constants/lineHeight'

type HeaderVariant = 'h1' | 'h2' | 'h3' | 'h4'

type BaseProps = HTMLAttributes<HTMLDivElement> &
  BorderProps &
  DisplayProps &
  SpaceProps &
  TestIdProps &
  TextAlignProps &
  TextColorProps

type Props = { variant?: HeaderVariant } & BaseProps

export default function PDSHeader({ testId, variant, ...props }: Props) {
  let HeaderComponent: StyledComponent<INTERNAL_Props>
  let headerStyles: typeof HEADER_STYLES[keyof typeof HEADER_STYLES]

  switch (variant) {
    case 'h1':
      HeaderComponent = BaseH1
      headerStyles = HEADER_STYLES.h1
      break
    case 'h2':
      HeaderComponent = BaseH2
      headerStyles = HEADER_STYLES.h2
      break
    case 'h3':
      HeaderComponent = BaseH3
      headerStyles = HEADER_STYLES.h3
      break
    case 'h4':
      HeaderComponent = BaseH4
      headerStyles = HEADER_STYLES.h4
      break
    default:
      HeaderComponent = BaseH1
      headerStyles = HEADER_STYLES.h1
      break
  }

  return (
    <HeaderComponent
      data-testid={testId}
      fontFamily="Inter"
      fontWeight={FONT_WEIGHT.fw600}
      {...headerStyles}
      {...props}
    />
  )
}

type INTERNAL_Props = BaseProps & TypographyProps

const BaseH1 = styled.h1<INTERNAL_Props>`
  ${border}
  ${color}
  ${display}
  ${space}
  ${typography}
`
const BaseH2 = styled.h2<INTERNAL_Props>`
  ${border}
  ${color}
  ${display}
  ${space}
  ${typography}
`
const BaseH3 = styled.h3<INTERNAL_Props>`
  ${border}
  ${color}
  ${display}
  ${space}
  ${typography}
`
const BaseH4 = styled.h4<INTERNAL_Props>`
  ${border}
  ${color}
  ${display}
  ${space}
  ${typography}
`

const HEADER_STYLES = {
  h1: {
    fontSize: FONT_SIZE.fs30,
    lineHeight: LINE_HEIGHT.lh36,
    letterSpacing: '-0.6px',
    // Bandaid: overrides the imported style sheet's margin
    margin: 0,
  },
  h2: {
    fontSize: FONT_SIZE.fs26,
    lineHeight: LINE_HEIGHT.lh32,
    letterSpacing: '-0.4px',
  },
  h3: {
    fontSize: FONT_SIZE.fs22,
    lineHeight: LINE_HEIGHT.lh28,
    letterSpacing: '-0.2px',
  },
  h4: {
    fontSize: FONT_SIZE.fs18,
    lineHeight: LINE_HEIGHT.lh24,
    letterSpacing: '-0.1px',
  },
}
